import React, { useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RichTextEditor } from '@/components/RichTextEditor';
import { ArrowLeft, Calendar, Eye, BookOpen, Loader2, MessageCircle } from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCustomerPortalArticle, useCustomerPortalArticlesByCollection, portalArticleParam } from '../../hooks/useKnowledgeBase';
import { useDateFormat } from '../../hooks/useDateFormat';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import type { CustomerPortalArticleDto } from '../../types/articles';
import { CustomerPortalKnowledgeBaseArticleSkeleton } from '../../components/LoadingSkeleton';
import { usePortalBranding } from '../../hooks/usePortalBranding';
import { checkCustomerAuthentication, type OrganizationInfo } from '../../lib/portal-loaders';
import { ApiRequestError } from '../../lib/apiErrors';
import { ArticleFeedbackPrompt } from '../components/ArticleFeedbackPrompt';
import { usePrivateKnowledgeBaseRedirect } from '../utils/knowledgeBaseAccess';
import { ArticleTableOfContents } from '../components/ArticleTableOfContents';
import { useArticleHeadings, MIN_TABLE_OF_CONTENTS_HEADINGS } from '../utils/useArticleHeadings';

interface CustomerPortalKnowledgeBaseArticleProps {
  // The article's slug, or its id (older links, ticket links)
  params: { articleId: string };
  organizationInfo?: OrganizationInfo;
  orgSlug?: string;
}

const RELATED_COUNT = 3;

export const CustomerPortalKnowledgeBaseArticle: React.FC<CustomerPortalKnowledgeBaseArticleProps> = ({ params, organizationInfo, orgSlug }) => {
  const { articleId } = params;
  const slug = orgSlug ?? organizationInfo?.subdomain ?? '';
  const { formatDateTime } = useDateFormat();
  const { getPrimaryWithOpacity } = usePortalBranding();

  // A private knowledge base sends visitors who aren't signed in to sign in, and back to this article afterwards
  const isKbPublic = organizationInfo?.portalSettings?.isKnowledgeBasePublic ?? true;
  const isAuthenticated = checkCustomerAuthentication(organizationInfo?.organizationId);
  const redirecting = usePrivateKnowledgeBaseRedirect(organizationInfo, isKbPublic, isAuthenticated);

  const { data: article, isLoading, error } = useCustomerPortalArticle(articleId);
  // The API answers 404 for an article that was unpublished, archived or deleted - an old link, not a failure
  const isGone = error instanceof ApiRequestError && error.status === 404;

  // An article opened by its id (an older link, a ticket's related article) moves to its slug address, the one
  // that's shared and indexed
  const navigate = useNavigate();
  useEffect(() => {
    if (article?.slug && articleId !== article.slug && slug) {
      navigate({
        to: '/org/$orgSlug/portal/knowledge-base/$articleId',
        params: { orgSlug: slug, articleId: article.slug },
        replace: true,
      });
    }
  }, [article?.slug, articleId, slug, navigate]);

  // The rendered content's headings, for "On this page"
  const contentRef = useRef<HTMLDivElement>(null);
  const headings = useArticleHeadings(contentRef, article?.content);

  // Set per-article SEO meta tags (title + description + og:*) while this page is mounted.
  // Public KB articles are reachable by search engines and shared externally, so each needs its own title
  // and description instead of falling back to the generic site title.
  const orgName = organizationInfo?.name;
  const metaDescription = article?.summary?.trim()
    || (article?.content
      ? article.content
          .replace(/<[^>]*>/g, " ")
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 160)
      : undefined);
  const withOrg = (title: string) => (orgName ? `${title} — ${orgName}` : title);
  useDocumentMeta({
    title: article
      ? withOrg(article.title)
      : isGone
        ? withOrg('Article not available')
        : error
          ? withOrg('Knowledge base')
          : 'Loading article…',
    description: metaDescription,
    // One address per article for search engines, whichever one this was opened at
    canonicalUrl: article && slug ? `${window.location.origin}/org/${slug}/portal/knowledge-base/${portalArticleParam(article)}` : undefined,
  });

  // Related articles: first the rest of the article's own section, then the rest of its collection (sections
  // included), so an article alone in its section still has some
  const sectionId = article?.collection?.parentId ? article.collection.id : undefined;
  const collectionId = article?.collection?.parentId ?? article?.collection?.id;
  const { data: sectionArticles } = useCustomerPortalArticlesByCollection(sectionId, !!sectionId, RELATED_COUNT + 1);
  const { data: collectionArticles } = useCustomerPortalArticlesByCollection(collectionId, !!collectionId, 10);

  if (redirecting) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const backLink = (
    <div className="flex items-center gap-3 sm:gap-4">
      <Button variant="ghost" size="sm" asChild className="text-sm">
        <Link to="/org/$orgSlug/portal/knowledge-base" params={{ orgSlug: slug }}>
          <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
          <span className="hidden min-[400px]:inline">Back to Knowledge Base</span>
          <span className="min-[400px]:hidden">Back</span>
        </Link>
      </Button>
    </div>
  );

  if (isLoading) {
    return <CustomerPortalKnowledgeBaseArticleSkeleton />;
  }

  if (error || !article) {
    return (
      <div className="space-y-4 sm:space-y-6">
        {backLink}
        <Alert>
          <AlertDescription>
            {isGone || !error
              ? "This article isn't available any more. It may have been moved or retired - try searching the knowledge base for what you need."
              : 'The article could not be loaded. Please try again later.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const seen = new Set<string>([article.id]);
  const relatedArticles: CustomerPortalArticleDto[] = [];
  for (const candidate of [...(sectionArticles?.articles ?? []), ...(collectionArticles?.articles ?? [])]) {
    if (relatedArticles.length === RELATED_COUNT) break;
    if (seen.has(candidate.id)) continue;
    seen.add(candidate.id);
    relatedArticles.push(candidate);
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {backLink}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Article Content */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader className="p-4 sm:p-6">
              <div className="space-y-2 sm:space-y-3">
                {article.collection && (
                  <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    {article.collection.parentId && article.collection.parentName && (
                      <>
                        <Link
                          to="/org/$orgSlug/portal/knowledge-base"
                          params={{ orgSlug: slug }}
                          search={{ collection: article.collection.parentId }}
                          className="hover:underline"
                        >
                          {article.collection.parentName}
                        </Link>
                        <span aria-hidden="true">›</span>
                      </>
                    )}
                    <Link
                      to="/org/$orgSlug/portal/knowledge-base"
                      params={{ orgSlug: slug }}
                      search={{ collection: article.collection.id }}
                    >
                      <Badge variant="outline" className="text-xs sm:text-sm cursor-pointer hover:bg-accent">{article.collection.name}</Badge>
                    </Link>
                  </nav>
                )}
                <CardTitle className="text-xl sm:text-2xl">
                  <h1>{article.title}</h1>
                </CardTitle>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center">
                    <Eye className="mr-1 h-3 w-3 sm:h-4 sm:w-4" aria-hidden="true" />
                    {article.viewCount?.toLocaleString() || '0'} views
                  </div>
                  <div className="flex items-center">
                    <Calendar className="mr-1 h-3 w-3 sm:h-4 sm:w-4" aria-hidden="true" />
                    Updated {article.updatedAt ? formatDateTime(article.updatedAt) : 'Recently'}
                  </div>
                  {article.authorName && (
                    <div className="text-gray-500">
                      by {article.authorName}
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {/* Article Summary */}
              {article.summary && (
                <div
                  className="mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg"
                  style={{
                    backgroundColor: getPrimaryWithOpacity(0.05),
                    border: `1px solid ${getPrimaryWithOpacity(0.2)}`
                  }}
                >
                  <p
                    className="text-sm sm:text-base font-medium"
                    style={{
                      color: getPrimaryWithOpacity(0.9)
                    }}
                  >
                    {article.summary}
                  </p>
                </div>
              )}

              {/* On small screens the contents sit above the article; on large ones, in the sidebar */}
              <ArticleTableOfContents
                headings={headings}
                className="lg:hidden mb-4 sm:mb-6 rounded-lg border border-gray-200 dark:border-gray-700 p-3 sm:p-4"
              />

              {/* Article Content */}
              <div ref={contentRef} className="prose prose-sm sm:prose-base lg:prose-lg xl:prose-xl max-w-none dark:prose-invert">
                <RichTextEditor
                  content={article.content}
                  onChange={() => {}}
                  editable={false}
                  className="border-none p-0"
                />
              </div>

              {/* Article Feedback - keyed by article so moving to a related article starts a fresh prompt */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <ArticleFeedbackPrompt key={article.id} articleId={article.id} />
              </div>

              {/* A way on when the article didn't answer the question */}
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 p-4">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Still need help?</p>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Our support team can help with anything this article doesn't cover.</p>
                </div>
                <Button variant="outline" size="sm" asChild className="flex-shrink-0">
                  <Link to="/org/$orgSlug/portal/submit-ticket" params={{ orgSlug: slug }}>
                    <MessageCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                    Submit a Ticket
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4 sm:space-y-6">
          {headings.length >= MIN_TABLE_OF_CONTENTS_HEADINGS && (
            <Card className="hidden lg:block lg:sticky lg:top-24">
              <CardContent className="p-4 sm:p-6">
                <ArticleTableOfContents headings={headings} />
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3">
              <CardTitle className="text-base sm:text-lg">
                <h2>Related Articles</h2>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
              {relatedArticles.length > 0 ? (
                <ul className="space-y-3">
                  {relatedArticles.map((relatedArticle) => (
                    <li key={relatedArticle.id}>
                      <Link
                        to="/org/$orgSlug/portal/knowledge-base/$articleId"
                        params={{ orgSlug: slug, articleId: portalArticleParam(relatedArticle) }}
                        className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md transition-colors"
                      >
                        <BookOpen className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <span className="text-sm sm:text-base text-gray-900 dark:text-white">{relatedArticle.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-4">
                  <BookOpen className="h-8 w-8 text-gray-400 mx-auto mb-2" aria-hidden="true" />
                  <p className="text-gray-600 dark:text-gray-400 text-sm">No related articles found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
